import {
  DATA_PERMISSION_DZ_TYPE,
  DATA_PERMISSION_IAM_TYPE,
  DATA_PERMISSION_LAKE_FORMATION_TYPE,
  DATA_PERMISSION_REDSHIFT_TYPE,
  DGS_SERVICE_NAME_GLADSTONE,
  DZ_GLUE_DATASOURCE_ID,
  DZ_REDSHIFT_DATASOURCE_ID,
  IAM_DATASOURCE_ID,
  LAKE_FORMATION_DATASOURCE_ID,
  REDSHIFT_DATASOURCE_ID,
  S3_TABLE_DATASOURCE_ID,
} from 'src/commons/constants';
import { generateSchemaId } from 'src/components/utils/hybridCatalog/idUtil';
import { getSchemas, listCatalogs, listDatabases, listDataSets } from 'src/api/catalog';
import {
  createCatalogDetailLink,
  createDatabaseDetailLink,
  createDatasetDetailLink,
  createSchemaDetailLink,
} from 'src/routes';
import { IAM_CATALOG_IDS } from 'src/commons/constants';
import { DataCatalogResource } from 'src/components/catalog/datasetdetail/datasetDetailHelpers';
import {
  RESOURCE_TYPE_DATABASE,
  RESOURCE_TYPE_SCHEMA,
  RESOURCE_TYPE_TABLE,
} from 'src/components/permissions/myBaselining/baseliningUtils';
import { generateGlueCatalogId } from 'src/components/common/resourceSelector/resourceSelector';

export const HYBRID_CATALOG_ARN_PREFIX = '^arn\\:.+\\:dgs-hc\\:.+\\:\\d{12}\\:';
export const AWS_ARN_PREFIX = '^arn\\:.+\\:.*\\:.+\\:\\d{12}\\:';
export const AWS_ARN_PREFIX_WITHOUT_TRAILING_COLON = '^arn\\:(aws(-[-a-z0-9]+)?)\\:.+\\:.*\\:\\d{12}';

export const HC_RESOURCE_ARN_REGEX = HYBRID_CATALOG_ARN_PREFIX + '.+[\\/A-Za-z0-9_-]*';
export const AWS_RESOURCE_ARN_REGEX = AWS_ARN_PREFIX + '.+[\\/A-Za-z0-9_-]*';

export const HC_DATASOURCES_REGEX = '(glue|glueLF|glueDZ|glueGalaxi|redshift|redshiftDZ|s3table)';
export const HC_REDSHIFT_DATASOURCES_REGEX = '(redshift|redshiftDZ)';

export const HC_CATALOG_ARN_REGEX = HYBRID_CATALOG_ARN_PREFIX + '.+';
export const HC_DATABASE_ARN_REGEX = HYBRID_CATALOG_ARN_PREFIX + HC_DATASOURCES_REGEX + '/database/.+$';
export const HC_DATASET_ARN_REGEX = HYBRID_CATALOG_ARN_PREFIX + HC_DATASOURCES_REGEX + '/database/.+/table/.+$';
export const HC_COLUMN_ARN_REGEX =
  HYBRID_CATALOG_ARN_PREFIX + HC_DATASOURCES_REGEX + '/database/.+/table/.+/column/.+$';

export const HC_REDSHIFT_CATALOG_ARN_REGEX = HYBRID_CATALOG_ARN_PREFIX + HC_REDSHIFT_DATASOURCES_REGEX + '/cluster/.+$';
export const HC_REDSHIFT_SRLS_CATALOG_ARN_REGEX =
  HYBRID_CATALOG_ARN_PREFIX + HC_REDSHIFT_DATASOURCES_REGEX + '/serverless/.+$';
export const HC_REDSHIFT_DATABASE_ARN_REGEX =
  HYBRID_CATALOG_ARN_PREFIX + HC_REDSHIFT_DATASOURCES_REGEX + '/cluster/.+/database/.+$';
export const HC_REDSHIFT_SRLS_DATABASE_ARN_REGEX =
  HYBRID_CATALOG_ARN_PREFIX + HC_REDSHIFT_DATASOURCES_REGEX + '/serverless/.+/database/.+$';
export const HC_REDSHIFT_SCHEMA_ARN_REGEX =
  HYBRID_CATALOG_ARN_PREFIX + HC_REDSHIFT_DATASOURCES_REGEX + '/cluster/.+/database/.+/schema/.+$';
export const HC_REDSHIFT_SRLS_SCHEMA_ARN_REGEX =
  HYBRID_CATALOG_ARN_PREFIX + HC_REDSHIFT_DATASOURCES_REGEX + '/serverless/.+/database/.+/schema/.+$';
export const HC_REDSHIFT_DATASET_ARN_REGEX =
  HYBRID_CATALOG_ARN_PREFIX + HC_REDSHIFT_DATASOURCES_REGEX + '/cluster/.+/database/.+/schema/.+/table/.+$';
export const HC_REDSHIFT_SRLS_DATASET_ARN_REGEX =
  HYBRID_CATALOG_ARN_PREFIX + HC_REDSHIFT_DATASOURCES_REGEX + '/serverless/.+/database/.+/schema/.+/table/.+$';
export const HC_REDSHIFT_COLUMN_ARN_REGEX =
  HYBRID_CATALOG_ARN_PREFIX + HC_REDSHIFT_DATASOURCES_REGEX + '/cluster/.+/database/.+/schema/.+/table/.+/column/.+$';
export const HC_REDSHIFT_SRLS_COLUMN_ARN_REGEX =
  HYBRID_CATALOG_ARN_PREFIX +
  HC_REDSHIFT_DATASOURCES_REGEX +
  '/serverless/.+/database/.+/schema/.+/table/.+/column/.+$';

export const AWS = 'aws';
export const CN_NORTHWEST_1 = 'cn-northwest-1';
export const AWS_CN = 'aws-cn';
export const US_ISO_EAST_1 = 'us-iso-east-1';
export const AWS_ISO = 'aws-iso';
export const US_ISOB_EAST_1 = 'us-isob-east-1';
export const AWS_ISO_B = 'aws-iso-b';
export const EU_ISOE_WEST_1 = 'eu-isoe-west-1';
export const AWS_ISO_E = 'aws-iso-e';
export const US_ISOF_SOUTH_1 = 'us-isof-south-1';
export const AWS_ISO_F = 'aws-iso-f';

export interface IdInfo {
  dataSource: string;
  catalogId: string;
  clusterId?: string;
  redshiftWorkgroupName?: string;
  bucketName?: string;
  databaseName?: string;
  schemaName?: string;
  tableName?: string;
  columnName?: string;
  region: string;
  fineGrainAccessPolicyId?: string;
}

export enum Scope {
  Catalog = 'Catalog',
  Database = 'Database',
  Schema = 'Schema',
  Table = 'Table',
  Column = 'Column',
  FineGrainedAccessPolicy = 'FineGrainedAccessPolicy',
}

export function isRedshift(idInfo: IdInfo) {
  return idInfo.dataSource == REDSHIFT_DATASOURCE_ID || idInfo.dataSource == DZ_REDSHIFT_DATASOURCE_ID;
}

export function isServerless(idInfo: IdInfo) {
  return !!idInfo.redshiftWorkgroupName;
}

export function getScopeForIdInfo(idInfo: IdInfo) {
  if (idInfo.fineGrainAccessPolicyId) {
    return Scope.FineGrainedAccessPolicy;
  } else if (idInfo.columnName) {
    return Scope.Column;
  } else if (idInfo.tableName) {
    return Scope.Table;
  } else if (idInfo.schemaName) {
    return Scope.Schema;
  } else if (idInfo.databaseName) {
    return Scope.Database;
  } else {
    return Scope.Catalog;
  }
}

export function getResourceNameForIdInfo(idInfo: IdInfo) {
  const scope = getScopeForIdInfo(idInfo);
  if (scope == Scope.Catalog) {
    return generateGlueCatalogId(idInfo.catalogId, idInfo.bucketName);
  } else if (scope == Scope.Database) {
    return idInfo.databaseName;
  } else if (scope == Scope.Schema) {
    return idInfo.schemaName;
  } else if (scope == Scope.Table) {
    return idInfo.tableName;
  } else if (scope == Scope.Column) {
    return idInfo.columnName;
  } else if (scope == Scope.FineGrainedAccessPolicy) {
    return idInfo.fineGrainAccessPolicyId;
  } else {
    return '';
  }
}

export function getScopeForId(id: string) {
  const idInfo = getIdInfoFromId(id);
  return getScopeForIdInfo(idInfo);
}

export function getScopeForArn(arn: string) {
  const idInfo = getIdInfoFromArn(arn);
  return getScopeForIdInfo(idInfo);
}

export function extractAttributeFromId(attributeName: string, id: string) {
  if (!id) {
    // return empty to handle undefined
    return '';
  }
  // special case: DS- is always first
  if (attributeName == 'DS') {
    return id.split('|')[0].substring(3);
  }
  if (!id.includes('|' + attributeName + '-')) {
    return undefined; // return undefined if the attribute does not exist
  }
  return id.split('|' + attributeName + '-')[1].split('|')[0];
}

export function extractAttributeFromArn(attributeName: string, arn: string) {
  if (!arn.includes('/' + attributeName + '/')) return undefined;
  return arn.split('/' + attributeName + '/')[1].split('/')[0];
}

export function getParentIdForId(id: string) {
  const idInfo: IdInfo = getIdInfoFromId(id);
  const scope: Scope = getScopeForId(id);
  if (scope == Scope.Column) {
    if (isRedshift(idInfo)) {
      if (isServerless(idInfo)) {
        return generateHcRedshiftServerlessDatasetId(idInfo);
      } else {
        return generateHcRedshiftDatasetId(idInfo);
      }
    } else {
      return generateHcGlueDatasetId(idInfo);
    }
  } else if (scope == Scope.Table) {
    if (isRedshift(idInfo)) {
      if (isServerless(idInfo)) {
        return generateHcRedshiftServerlessSchemaId(idInfo);
      } else {
        return generateHcRedshiftSchemaId(idInfo);
      }
    } else {
      return generateHcGlueDatabaseId(idInfo);
    }
  } else if (scope == Scope.Schema) {
    if (isServerless(idInfo)) {
      return generateHcRedshiftServerlessDatabaseId(idInfo);
    } else {
      return generateHcRedshiftDatabaseId(idInfo);
    }
  } else if (scope == Scope.Database) {
    if (isRedshift(idInfo)) {
      if (isServerless(idInfo)) {
        return generateHcRedshiftServerlessCatalogId(idInfo);
      } else {
        return generateHcRedshiftCatalogId(idInfo);
      }
    } else {
      return generateHcGlueCatalogId(idInfo);
    }
  }
}

export function getIdInfoFromId(id: string): IdInfo {
  return {
    dataSource: extractAttributeFromId('DS', id),
    catalogId: extractAttributeFromId('A', id),
    clusterId: extractAttributeFromId('CI', id),
    redshiftWorkgroupName: extractAttributeFromId('WN', id),
    bucketName: extractAttributeFromId('BN', id),
    databaseName: extractAttributeFromId('DN', id),
    schemaName: extractAttributeFromId('SN', id),
    tableName: extractAttributeFromId('TN', id),
    columnName: extractAttributeFromId('CN', id),
    region: extractAttributeFromId('R', id),
  };
}

export function getIdInfoFromArn(arn: string) {
  if (!arn) return {} as IdInfo;
  // example arn: arn:aws:dgs-hc:us-west-2:375937567384:redshift/cluster/redshift-test/database/awsdw/schema/awsdl_processed/table/o_aws_regions
  // Fine grain policy arn: arn:aws:dgs-gladstone:us-east-1:284697478245:glueLF/database/akaanj_lf_local_db/table/states/fine-grained-access-policy/fgap-8ed267a5a496475795340cdbfaa46f2

  const region = arn.split(':')[3];
  const dataSource = arn.split(':')[5]?.split('/')[0];
  const catalogId = arn.split(':')[4];
  const clusterId = extractAttributeFromArn('cluster', arn);
  const redshiftWorkgroupName = extractAttributeFromArn('serverless', arn);
  const bucketName = extractAttributeFromArn('bucket', arn);
  const databaseName = extractAttributeFromArn('database', arn);
  const schemaName = extractAttributeFromArn('schema', arn);
  const tableName = extractAttributeFromArn('table', arn);
  const columnName = extractAttributeFromArn('column', arn);
  const fgapId = extractAttributeFromArn('fine-grained-access-policy', arn);

  return {
    dataSource: dataSource,
    catalogId: catalogId,
    clusterId: clusterId,
    redshiftWorkgroupName: redshiftWorkgroupName,
    bucketName: bucketName,
    databaseName: databaseName,
    schemaName: schemaName,
    tableName: tableName,
    columnName: columnName,
    region: region,
    fineGrainAccessPolicyId: fgapId,
  } as IdInfo;
}

export function hcArnPrefix(idInfo: IdInfo) {
  let prefix = `arn:${getAwsPartitionForRegion(idInfo.region)}:dgs-hc:${idInfo.region}:${idInfo.catalogId}:${
    idInfo.dataSource
  }`;
  if (idInfo.bucketName) {
    prefix = prefix + `/bucket/${idInfo.bucketName}`;
  }
  return prefix;
}

export function hcIdPrefix(idInfo: IdInfo) {
  if (idInfo.bucketName) {
    return `DS-${idInfo.dataSource}|A-${idInfo.catalogId}|BN-${idInfo.bucketName}`;
  }
  return `DS-${idInfo.dataSource}|A-${idInfo.catalogId}`;
}

export function generateHcGlueDatasetArn(idInfo: IdInfo) {
  return hcArnPrefix(idInfo) + `/database/${idInfo.databaseName}/table/${idInfo.tableName}`;
}

export function generateGladstoneGlueFGAPArn(idInfo: IdInfo) {
  // eg: arn:aws:dgs-gladstone:us-east-1:284697478245:glueLF/database/akaanj_lf_local_db/table/states/fine-grained-access-policy/fgap-8ed267a5a496475795340cdbfaa46f2
  return (
    hcArnPrefix(idInfo) +
    `/database/${idInfo.databaseName}/table/${idInfo.tableName}/fine-grained-access-policy/${idInfo.fineGrainAccessPolicyId}`
  ).replace('dgs-hc', 'dgs-gladstone');
}

export function generateGlueDatasetArn(idInfo: IdInfo) {
  // https://docs.aws.amazon.com/glue/latest/dg/glue-specifying-resource-arns.html
  if (idInfo.bucketName) {
    return (
      generateGlueCatalogArnPrefix(idInfo) +
      `:table/s3tablescatalog/${idInfo.bucketName}/${idInfo.databaseName}/${idInfo.tableName}`
    );
  }
  return (
    `arn:${getAwsPartitionForRegion(idInfo.region)}:glue:${idInfo.region}:${idInfo.catalogId}:` +
    `table/${idInfo.databaseName}/${idInfo.tableName}`
  );
}
export function generateGlueDatabaseArn(idInfo: IdInfo) {
  if (idInfo.bucketName) {
    return (
      generateGlueCatalogArnPrefix(idInfo) + `:database/s3tablescatalog/${idInfo.bucketName}/${idInfo.databaseName}`
    );
  }
  return (
    `arn:${getAwsPartitionForRegion(idInfo.region)}:glue:${idInfo.region}:${idInfo.catalogId}:` +
    `database/${idInfo.databaseName}`
  );
}

export function generateHcGlueDatasetId(idInfo: IdInfo) {
  return hcIdPrefix(idInfo) + `|DN-${idInfo.databaseName}|TN-${idInfo.tableName}|R-${idInfo.region}`;
}

export function generateHcGlueDatabaseArn(idInfo: IdInfo) {
  return hcArnPrefix(idInfo) + `/database/${idInfo.databaseName}`;
}

export function generateGlueCatalogArnPrefix(idInfo: IdInfo) {
  // arn:aws:glue:us-east-1:123456789012
  return `arn:${getAwsPartitionForRegion(idInfo.region)}:glue:${idInfo.region}:${idInfo.catalogId}`;
}
export function generateGlueCatalogArnForPrefixLookup(idInfo: IdInfo) {
  // arn:aws:glue:us-east-1:123456789012:bucket/my-bucket
  // arn:aws:glue:us-east-1:123456789012
  if (idInfo.bucketName) {
    return `arn:${getAwsPartitionForRegion(idInfo.region)}:glue:${idInfo.region}:${idInfo.catalogId}:bucket/${
      idInfo.bucketName
    }`;
  }
  return generateGlueCatalogArnPrefix(idInfo);
}
export function generateGlueDatabaseArnForPrefixLookup(idInfo: IdInfo) {
  // arn:aws:glue:us-east-1:123456789012:bucket/my-bucket/database/dbName
  // arn:aws:glue:us-east-1:123456789012:database/dbName
  if (idInfo.bucketName) {
    return generateGlueCatalogArnForPrefixLookup(idInfo) + '/database/' + idInfo.databaseName;
  }
  return generateGlueCatalogArnForPrefixLookup(idInfo) + ':database/' + idInfo.databaseName;
}
export function generateGlueDatasetArnForPrefixLookup(idInfo: IdInfo) {
  // arn:aws:glue:us-east-1:123456789012:bucket/my-bucket/database/dbName/table/tableName
  // arn:aws:glue:us-east-1:123456789012:database/dbName/table/tableName
  return generateGlueDatabaseArnForPrefixLookup(idInfo) + `/table/${idInfo.tableName}`;
}

export function generateHcGlueDatabaseId(idInfo: IdInfo) {
  return hcIdPrefix(idInfo) + `|DN-${idInfo.databaseName}|R-${idInfo.region}`;
}

export function generateHcGlueCatalogArn(idInfo: IdInfo) {
  return hcArnPrefix(idInfo);
}

export function generateHcGlueCatalogId(idInfo: IdInfo) {
  return hcIdPrefix(idInfo) + `|R-${idInfo.region}`;
}

// REDSHIFT ARNS AND IDS

export function generateHcRedshiftColumnArn(idInfo: IdInfo) {
  return generateHcRedshiftServerlessDatasetArn(idInfo) + `/column/${idInfo.columnName}`;
}

export function generateHcRedshiftServerlessColumnArn(idInfo: IdInfo) {
  return generateHcRedshiftServerlessDatasetArn(idInfo) + `/column/${idInfo.columnName}`;
}

export function generateHcRedshiftDatasetArn(idInfo: IdInfo) {
  return generateHcRedshiftSchemaArn(idInfo) + `/table/${idInfo.tableName}`;
}

export function generateGladstoneRedshiftFGAPArn(idInfo: IdInfo) {
  // eg: arn:aws:dgs-gladstone:us-west-2:375937567384:redshift/cluster/redshift-test/database/awsdw/schema/awsdl_processed/table/o_aws_regions/fine-grained-access-policy/fgap-8ed267a5a496475795340cdbfaa46f2
  return (
    generateHcRedshiftDatasetArn(idInfo) + `/fine-grained-access-policy/${idInfo.fineGrainAccessPolicyId}`
  ).replace('dgs-hc', 'dgs-gladstone');
}

export function generateHcRedshiftServerlessDatasetArn(idInfo: IdInfo) {
  return generateHcRedshiftServerlessSchemaArn(idInfo) + `/table/${idInfo.tableName}`;
}

export function generateGladstoneRedshiftServerlessFGAPArn(idInfo: IdInfo) {
  // eg: arn:aws:dgs-gladstone:us-west-2:375937567384:redshift/serverless/redshift-test/database/awsdw/schema/awsdl_processed/table/o_aws_regions/fine-grained-access-policy/fgap-8ed267a5a496475795340cdbfaa46f2
  return (
    generateHcRedshiftServerlessDatasetArn(idInfo) + `/fine-grained-access-policy/${idInfo.fineGrainAccessPolicyId}`
  ).replace('dgs-hc', 'dgs-gladstone');
}

export function generateHcRedshiftDatasetId(idInfo: IdInfo) {
  return (
    hcIdPrefix(idInfo) +
    `|CI-${idInfo.clusterId}|DN-${idInfo.databaseName}|SN-${idInfo.schemaName}|TN-${idInfo.tableName}|R-${idInfo.region}`
  );
}

export function generateHcRedshiftServerlessDatasetId(idInfo: IdInfo) {
  return (
    hcIdPrefix(idInfo) +
    `|WN-${idInfo.redshiftWorkgroupName}|DN-${idInfo.databaseName}|SN-${idInfo.schemaName}|TN-${idInfo.tableName}|R-${idInfo.region}`
  );
}

export function generateHcRedshiftSchemaArn(idInfo: IdInfo) {
  return generateHcRedshiftDatabaseArn(idInfo) + `/schema/${idInfo.schemaName}`;
}

export function generateHcRedshiftServerlessSchemaArn(idInfo: IdInfo) {
  return generateHcRedshiftServerlessDatabaseArn(idInfo) + `/schema/${idInfo.schemaName}`;
}

export function generateHcRedshiftSchemaId(idInfo: IdInfo) {
  return (
    hcIdPrefix(idInfo) + `|CI-${idInfo.clusterId}|DN-${idInfo.databaseName}|SN-${idInfo.schemaName}|R-${idInfo.region}`
  );
}

export function generateHcRedshiftServerlessSchemaId(idInfo: IdInfo) {
  return (
    hcIdPrefix(idInfo) +
    `|WN-${idInfo.redshiftWorkgroupName}|DN-${idInfo.databaseName}|SN-${idInfo.schemaName}|R-${idInfo.region}`
  );
}

export function generateHcRedshiftDatabaseArn(idInfo: IdInfo) {
  return generateHcRedshiftCatalogArn(idInfo) + `/database/${idInfo.databaseName}`;
}

export function generateHcRedshiftServerlessDatabaseArn(idInfo: IdInfo) {
  return generateHcRedshiftServerlessCatalogArn(idInfo) + `/database/${idInfo.databaseName}`;
}

export function generateHcRedshiftDatabaseId(idInfo: IdInfo) {
  return hcIdPrefix(idInfo) + `|CI-${idInfo.clusterId}|DN-${idInfo.databaseName}|R-${idInfo.region}`;
}

export function generateHcRedshiftServerlessDatabaseId(idInfo: IdInfo) {
  return hcIdPrefix(idInfo) + `|WN-${idInfo.redshiftWorkgroupName}|DN-${idInfo.databaseName}|R-${idInfo.region}`;
}

export function generateHcRedshiftCatalogArn(idInfo: IdInfo) {
  return hcArnPrefix(idInfo) + `/cluster/${idInfo.clusterId}`;
}

export function generateHcRedshiftServerlessCatalogArn(idInfo: IdInfo) {
  return hcArnPrefix(idInfo) + `/serverless/${idInfo.redshiftWorkgroupName}`;
}

export function generateHcRedshiftCatalogId(idInfo: IdInfo) {
  return hcIdPrefix(idInfo) + `|R-${idInfo.region}`;
}

export function generateHcRedshiftServerlessCatalogId(idInfo: IdInfo) {
  return hcIdPrefix(idInfo) + `|R-${idInfo.region}`;
}

export function generateGlueArnFromId(id: string) {
  const scope = getScopeForId(id);
  const idInfo = getIdInfoFromId(id);

  if (scope == Scope.Table) {
    return generateGlueDatasetArn(idInfo);
  } else if (scope == Scope.Database) {
    return generateGlueDatabaseArn(idInfo);
  }
  return '';
}

export function generateGlueArnForPrefixLookupFromId(id: string) {
  const scope = getScopeForId(id);
  const idInfo = getIdInfoFromId(id);

  if (scope == Scope.Table) {
    return generateGlueDatasetArnForPrefixLookup(idInfo);
  } else if (scope == Scope.Database) {
    return generateGlueDatabaseArnForPrefixLookup(idInfo);
  } else if (scope == Scope.Catalog) {
    return generateGlueCatalogArnPrefix(idInfo);
  }
  return '';
}

export function generateArnFromIdAndScope(scope: Scope, idInfo: IdInfo) {
  if (scope == Scope.FineGrainedAccessPolicy) {
    if (isRedshift(idInfo)) {
      if (isServerless(idInfo)) {
        return generateGladstoneRedshiftServerlessFGAPArn(idInfo);
      } else {
        return generateGladstoneRedshiftFGAPArn(idInfo);
      }
    } else {
      return generateGladstoneGlueFGAPArn(idInfo);
    }
  }
  if (scope == Scope.Table) {
    if (isRedshift(idInfo)) {
      if (isServerless(idInfo)) {
        return generateHcRedshiftServerlessDatasetArn(idInfo);
      } else {
        return generateHcRedshiftDatasetArn(idInfo);
      }
    } else {
      return generateHcGlueDatasetArn(idInfo);
    }
  } else if (scope == Scope.Database) {
    if (isRedshift(idInfo)) {
      if (isServerless(idInfo)) {
        return generateHcRedshiftServerlessDatabaseArn(idInfo);
      } else {
        return generateHcRedshiftDatabaseArn(idInfo);
      }
    } else {
      return generateHcGlueDatabaseArn(idInfo);
    }
  } else if (scope == Scope.Catalog) {
    if (isRedshift(idInfo)) {
      if (isServerless(idInfo)) {
        return generateHcRedshiftServerlessCatalogArn(idInfo);
      } else {
        return generateHcRedshiftCatalogArn(idInfo);
      }
    } else {
      return generateHcGlueCatalogArn(idInfo);
    }
  } else if (scope == Scope.Schema) {
    if (isServerless(idInfo)) {
      return generateHcRedshiftServerlessSchemaArn(idInfo);
    } else {
      return generateHcRedshiftSchemaArn(idInfo);
    }
  }
  return '';
}

export function generateArnFromId(id: string) {
  const scope = getScopeForId(id);
  const idInfo = getIdInfoFromId(id);
  return generateArnFromIdAndScope(scope, idInfo);
}

export function generateIdFromIdInfo(idInfo: IdInfo) {
  const scope: Scope = getScopeForIdInfo(idInfo);
  if (scope == Scope.Table) {
    if (isRedshift(idInfo)) {
      if (isServerless(idInfo)) {
        return generateHcRedshiftServerlessDatasetId(idInfo);
      } else {
        return generateHcRedshiftDatasetId(idInfo);
      }
    } else {
      return generateHcGlueDatasetId(idInfo);
    }
  } else if (scope == Scope.Database) {
    if (isRedshift(idInfo)) {
      if (isServerless(idInfo)) {
        return generateHcRedshiftServerlessDatabaseId(idInfo);
      } else {
        return generateHcRedshiftDatabaseId(idInfo);
      }
    } else {
      return generateHcGlueDatabaseId(idInfo);
    }
  } else if (scope == Scope.Catalog) {
    if (isRedshift(idInfo)) {
      if (isServerless(idInfo)) {
        return generateHcRedshiftServerlessCatalogId(idInfo);
      } else {
        return generateHcRedshiftCatalogId(idInfo);
      }
    } else {
      return generateHcGlueCatalogId(idInfo);
    }
  } else if (scope == Scope.Schema) {
    if (isServerless(idInfo)) {
      return generateHcRedshiftServerlessSchemaId(idInfo);
    } else {
      return generateHcRedshiftSchemaId(idInfo);
    }
  }
  return '';
}

export function generateIdFromArn(arn: string) {
  const idInfo = getIdInfoFromArn(arn);

  return generateIdFromIdInfo(idInfo);
}

export function getAwsPartitionForRegion(region: string) {
  switch (region) {
    case CN_NORTHWEST_1:
      return AWS_CN;
    case US_ISO_EAST_1:
      return AWS_ISO;
    case US_ISOB_EAST_1:
      return AWS_ISO_B;
    case EU_ISOE_WEST_1:
      return AWS_ISO_E;
    case US_ISOF_SOUTH_1:
      return AWS_ISO_F;
    default:
      return AWS;
  }
}

export function convertResourceARNToLink(arn: string) {
  const scope = getScopeForArn(arn);
  const idInfo = getIdInfoFromArn(arn);

  if (scope == Scope.Catalog) {
    return createCatalogDetailLink(
      idInfo.dataSource,
      idInfo.catalogId,
      idInfo.clusterId,
      idInfo.redshiftWorkgroupName,
      idInfo.bucketName,
      idInfo.region,
    );
  }
  if (scope == Scope.Database) {
    return createDatabaseDetailLink(
      idInfo.dataSource,
      idInfo.catalogId,
      idInfo.clusterId,
      idInfo.redshiftWorkgroupName,
      idInfo.bucketName,
      idInfo.databaseName,
      idInfo.region,
    );
  }
  if (scope == Scope.Table || scope == Scope.FineGrainedAccessPolicy) {
    const datasetId = isRedshift(idInfo) ? generateHcRedshiftDatasetId(idInfo) : generateHcGlueDatasetId(idInfo);
    return createDatasetDetailLink(datasetId);
  }
  if (scope == Scope.Schema) {
    return createSchemaDetailLink(
      idInfo.dataSource,
      idInfo.catalogId,
      idInfo.clusterId,
      idInfo.redshiftWorkgroupName,
      idInfo.databaseName,
      idInfo.schemaName,
      idInfo.region,
    );
  }
  return '';
}

export const dpTypeForDataSource = (dataSource: string) => {
  if (dataSource == IAM_DATASOURCE_ID) {
    return DATA_PERMISSION_IAM_TYPE;
  } else if (dataSource == LAKE_FORMATION_DATASOURCE_ID || dataSource == S3_TABLE_DATASOURCE_ID) {
    return DATA_PERMISSION_LAKE_FORMATION_TYPE;
  } else if (dataSource == REDSHIFT_DATASOURCE_ID) {
    return DATA_PERMISSION_REDSHIFT_TYPE;
  } else if (dataSource == DZ_REDSHIFT_DATASOURCE_ID || dataSource == DZ_GLUE_DATASOURCE_ID) {
    return DATA_PERMISSION_DZ_TYPE;
  }
  return '';
};

export const convertResourceArnToDpResource = (arn: string) => {
  const idInfo: IdInfo = getIdInfoFromArn(arn);
  const id: string = generateIdFromArn(arn);
  const scope: string = getScopeForArn(arn);

  const typeForScope = (scope: String) => {
    if (scope == Scope.Table) {
      return RESOURCE_TYPE_TABLE;
    } else if (scope == Scope.Database) {
      return RESOURCE_TYPE_DATABASE;
    } else if (scope == Scope.Schema) {
      return RESOURCE_TYPE_SCHEMA;
    }
    return '';
  };

  const resource: DataCatalogResource = {
    idInfo: idInfo,
    accountId: idInfo.catalogId,
    region: idInfo.region,
    type: typeForScope(scope),
    dataCatalogObjectDetails: {
      dataSourceId: idInfo.dataSource,
      clusterIdentifier: idInfo.clusterId,
      redshiftWorkgroupName: idInfo.redshiftWorkgroupName,
      databaseName: idInfo.databaseName,
      schemaName: idInfo.schemaName,
      tableName: idInfo.tableName,
    },
    dpType: dpTypeForDataSource(idInfo.dataSource),
    tagResourceId: id,
    tagUpperLevelResourceId:
      idInfo.dataSource === LAKE_FORMATION_DATASOURCE_ID ? generateHcGlueDatabaseId(idInfo) : generateSchemaId(idInfo),
  };

  return resource;
};

// TODO
export const userOwnsResource = async (userInfo: any, idInfo: IdInfo) => {
  const scope = getScopeForIdInfo(idInfo);
  let owners = [];
  try {
    if (scope == Scope.Table) {
      const id = isRedshift(idInfo)
        ? isServerless(idInfo)
          ? generateHcRedshiftServerlessDatasetId(idInfo)
          : generateHcRedshiftDatasetId(idInfo)
        : generateHcGlueDatasetId(idInfo);
      const dataSetResult = await listDataSets({
        Filter: {
          IdList: [id],
        },
      });
      owners = dataSetResult.DataSetList[0].Owners;
      owners.push(dataSetResult.DataSetList[0].PrimaryOwner);
    } else if (scope == Scope.Database) {
      const databaseResult = await listDatabases({
        DatabaseKeyList: [
          {
            CatalogId: idInfo.catalogId,
            DatabaseName: idInfo.databaseName,
            ClusterIdentifier: idInfo.clusterId,
            RedshiftWorkgroupName: idInfo.redshiftWorkgroupName,
            Region: idInfo.region,
            DataSourceId: idInfo.dataSource,
          },
        ],
      });
      owners = databaseResult.DatabaseInfoList[0].Owners;
    } else if (scope == Scope.Catalog) {
      const catalogResult = await listCatalogs({
        Filter: {
          CatalogKeyList: [
            {
              CatalogId: idInfo.catalogId,
              Region: idInfo.region,
              ClusterIdentifier: idInfo.clusterId,
              RedshiftWorkgroupName: idInfo.redshiftWorkgroupName,
              DataSourceId: idInfo.dataSource,
            },
          ],
        },
      });
      owners = [catalogResult.CatalogInfoList[0].Owner];
    } else if (scope == Scope.Schema) {
      const schemaResult = await getSchemas({
        SchemaKeyList: [
          {
            CatalogId: idInfo.catalogId,
            DatabaseName: idInfo.databaseName,
            Schema: idInfo.schemaName,
            DataSourceId: idInfo.dataSource,
            Region: idInfo.region,
            ClusterIdentifier: idInfo.clusterId,
            RedshiftWorkgroupName: idInfo.redshiftWorkgroupName,
          },
        ],
      });
      owners = schemaResult.SchemaInfoList[0].Owners;
    }
  } catch (e) {
    console.log('Failed to fetch ownership for idInfo: ', idInfo, e);
    return false;
  }

  const groupOwner = userInfo?.memberGroupIds?.some((e) => owners.includes(e));
  const workspaceOwner = userInfo?.memberWorkspaceIds?.some((e) => owners.includes(e));
  return groupOwner || workspaceOwner;
};

// Gladstone
export function generateFGAPArn(policy) {
  if (!policy || !policy.tableData) return null;

  const policyId = policy.id;
  const tableData = policy.tableData;

  if (tableData.clusterId) {
    return (
      `arn:${getAwsPartitionForRegion(tableData.region)}:${DGS_SERVICE_NAME_GLADSTONE}:${tableData.region}:${
        tableData.tableCatalogId
      }:${REDSHIFT_DATASOURCE_ID}` +
      `/cluster/${tableData.clusterId}/database/${tableData.databaseName}/schema/${tableData.schemaName}/table/${tableData.tableName}/fine-grained-access-policy/${policyId}`
    );
  } else if (tableData.bucketName) {
    return (
      `arn:${getAwsPartitionForRegion(tableData.region)}:${DGS_SERVICE_NAME_GLADSTONE}:${tableData.region}:${
        tableData.tableCatalogId
      }:${S3_TABLE_DATASOURCE_ID}` +
      `/bucket/${tableData.bucketName}/database/${tableData.databaseName}/table/${tableData.tableName}/fine-grained-access-policy/${policyId}`
    );
  }

  return (
    `arn:${getAwsPartitionForRegion(tableData.region)}:${DGS_SERVICE_NAME_GLADSTONE}:${tableData.region}:${
      tableData.tableCatalogId
    }:${LAKE_FORMATION_DATASOURCE_ID}` +
    `/database/${tableData.databaseName}/table/${tableData.tableName}/fine-grained-access-policy/${policyId}`
  );
}

// ----------------------------------------------- Arn Utils -------------------------------------------------

// HybridCatalogId to AWS resource arn format
export function getAWSResourceArnFromHybridCatalogId(hybridCatalogId: string) {
  const scope = getScopeForId(hybridCatalogId);
  const idInfo = getIdInfoFromId(hybridCatalogId);

  if (scope == Scope.Table) {
    return generateAWSTableResourceArn(idInfo);
  } else if (scope == Scope.Schema) {
    return generateAWSSchemaResourceArn(idInfo);
  } else if (scope == Scope.Database) {
    return generateAWSDatabaseResourceArn(idInfo);
  }
  return '';
}

function generateAWSTableResourceArn(idInfo: IdInfo) {
  if (idInfo?.clusterId) {
    // redshift
    return (
      `arn:${getAwsPartitionForRegion(idInfo?.region)}:redshift:${idInfo?.region}:${idInfo?.catalogId}:` +
      `table/${idInfo?.clusterId}/${idInfo?.databaseName}/${idInfo?.schemaName}/${idInfo?.tableName}`
    );
  } else {
    // glue
    return (
      `arn:${getAwsPartitionForRegion(idInfo?.region)}:glue:${idInfo?.region}:${idInfo?.catalogId}:` +
      `table/${idInfo?.databaseName}/${idInfo?.tableName}`
    );
  }
}

function generateAWSSchemaResourceArn(idInfo: IdInfo) {
  // redshift
  return (
    `arn:${getAwsPartitionForRegion(idInfo?.region)}:redshift:${idInfo?.region}:${idInfo?.catalogId}:` +
    `schema/${idInfo?.clusterId}/${idInfo?.databaseName}/${idInfo?.schemaName}`
  );
}

function generateAWSDatabaseResourceArn(idInfo: IdInfo) {
  if (idInfo?.clusterId) {
    // redshift
    return (
      `arn:${getAwsPartitionForRegion(idInfo?.region)}:redshift:${idInfo?.region}:${idInfo?.catalogId}:` +
      `database/${idInfo?.clusterId}/${idInfo?.databaseName}`
    );
  } else {
    // glue
    return generateGlueDatabaseArn(idInfo);
  }
}

// HybridCatalogId to HybridCatalog resource arn format
export function getHybridCatalogResourceArnFromHybridCatalogId(hybridCatalogId: string) {
  const scope = getScopeForId(hybridCatalogId);
  const idInfo = getIdInfoFromId(hybridCatalogId);

  if (scope == Scope.Table) {
    return generateHybridCatalogTableResourceArn(idInfo);
  } else if (scope == Scope.Schema) {
    return generateHybridCatalogSchemaResourceArn(idInfo);
  } else if (scope == Scope.Database) {
    return generateHybridCatalogDatabaseResourceArn(idInfo);
  }
  return '';
}

function generateHybridCatalogArnPrefix(idInfo: IdInfo) {
  return `arn:${getAwsPartitionForRegion(idInfo?.region)}:dgs-hc:${idInfo?.region}:${idInfo?.catalogId}:${
    idInfo?.dataSource
  }`;
}

function generateHybridCatalogTableResourceArn(idInfo: IdInfo) {
  if (idInfo?.clusterId) {
    // redshift | redshiftDZ
    return (
      generateHybridCatalogArnPrefix(idInfo) +
      `/cluster/${idInfo?.clusterId}/database/${idInfo?.databaseName}/schema/${idInfo?.schemaName}/table/${idInfo?.tableName}`
    );
  } else if (idInfo?.bucketName) {
    // s3table
    return (
      generateHybridCatalogArnPrefix(idInfo) +
      `/bucket/${idInfo?.bucketName}/database/${idInfo?.databaseName}/table/${idInfo?.tableName}`
    );
  } else {
    // glue | glueLF | glueDZ
    return generateHybridCatalogArnPrefix(idInfo) + `/database/${idInfo?.databaseName}/table/${idInfo?.tableName}`;
  }
}

function generateHybridCatalogSchemaResourceArn(idInfo: IdInfo) {
  // redshift
  return (
    generateHybridCatalogArnPrefix(idInfo) +
    `/cluster/${idInfo?.clusterId}/database/${idInfo?.databaseName}/schema/${idInfo?.schemaName}`
  );
}

function generateHybridCatalogDatabaseResourceArn(idInfo: IdInfo) {
  if (idInfo?.clusterId) {
    // redshift
    return generateHybridCatalogArnPrefix(idInfo) + `/cluster/${idInfo?.clusterId}/database/${idInfo?.databaseName}`;
  } else if (idInfo?.bucketName) {
    return generateHybridCatalogArnPrefix(idInfo) + `/bucket/${idInfo?.bucketName}/database/${idInfo?.databaseName}`;
  } else {
    return generateHybridCatalogArnPrefix(idInfo) + `/database/${idInfo?.databaseName}`;
  }
}

// AWS resource arn to HybridCatalogId  format
export function getHybridCatalogIdFromAWSResourceArn(awsResourceArn: string) {
  try {
    const scope = getScopeForAwsArn(awsResourceArn);
    const idInfo = getIdInfoFromAwsArn(awsResourceArn);
    if (scope == Scope.Table) {
      return generateHybridCatalogTableResourceId(idInfo);
    } else if (scope == Scope.Schema) {
      return generateHybridCatalogSchemaResourceId(idInfo);
    } else if (scope == Scope.Database) {
      return generateHybridCatalogDatabaseResourceId(idInfo);
    }
    return undefined;
  } catch (e) {
    console.log(`Failed to convert Arn:${awsResourceArn} to HybridCatalogId. Reason:${e} `);
    return undefined;
  }
}

function generateHybridCatalogTableResourceId(idInfo: IdInfo) {
  if (idInfo?.clusterId) {
    return generateHcRedshiftDatasetId(idInfo);
  } else if (idInfo?.redshiftWorkgroupName) {
    return generateHcRedshiftServerlessDatasetId(idInfo);
  } else {
    if (IAM_CATALOG_IDS.includes(idInfo.catalogId)) {
      // glue
      return `DS-glue|A-${idInfo.catalogId}|DN-${idInfo.databaseName}|TN-${idInfo.tableName}|R-${idInfo.region}`;
    }
    return generateHcGlueDatasetId(idInfo);
  }
}

function generateHybridCatalogSchemaResourceId(idInfo: IdInfo) {
  if (idInfo?.redshiftWorkgroupName) {
    return generateHcRedshiftServerlessSchemaId(idInfo);
  }
  return generateHcRedshiftSchemaId(idInfo);
}

function generateHybridCatalogDatabaseResourceId(idInfo: IdInfo) {
  if (idInfo?.clusterId) {
    return generateHcRedshiftDatabaseId(idInfo);
  } else if (idInfo?.redshiftWorkgroupName) {
    return generateHcRedshiftServerlessDatabaseId(idInfo);
  } else {
    return generateHcGlueDatabaseId(idInfo);
  }
}

function getScopeForAwsArn(awsArn: string) {
  const idInfo = getIdInfoFromAwsArn(awsArn);
  return getScopeForIdInfo(idInfo);
}

function getIdInfoFromAwsArn(awsArn: string): IdInfo {
  /**
   * example arns:
   * Glue Column: arn:aws:glue:us-west-2:375937567384:column/db_name/table_name/column_name
   * Glue Table: arn:aws:glue:us-west-2:375937567384:table/db_name/table_name
   * Glue database: arn:aws:glue:us-west-2:375937567384:database/db_name
   * S3 Table Column: arn:aws:glue:us-west-2:375937567384:column/s3tablescatalog/bucket_name/db_name/table_name/column_name
   * S3 Table Table: arn:aws:glue:us-west-2:375937567384:table/s3tablescatalog/bucket_name/db_name/table_name
   * S3 Table database: arn:aws:glue:us-west-2:375937567384:database/s3tablescatalog/bucket_name/db_name
   *
   * Redshift Column: arn:aws:redshift:us-west-2:375937567384:column/cluster_name/db_name/schema_name/table_name/column_name
   * Redshift Table: arn:aws:redshift:us-west-2:375937567384:table/cluster_name/db_name/schema_name/table_name
   * Redshift Schema: arn:aws:redshift:us-west-2:375937567384:schema/cluster_name/db_name/schema_name
   * Redshift Db: arn:aws:redshift:us-west-2:375937567384:database/cluster_name/db_name
   *
   * S3 bucket: arn:aws:s3:::bucket_name
   */
  const arnParts = awsArn.split(':');
  let region = arnParts[3];
  let dataSource = arnParts[2];
  let catalogId = arnParts[4];
  let clusterId = '';
  let bucketName = '';
  let databaseName = '';
  let schemaName = '';
  let tableName = '';
  let columnName = '';

  if (arnParts.length > 5) {
    const resourceParts = arnParts[5].split('/');
    switch (dataSource.toLowerCase()) {
      case 'glue':
        switch (resourceParts[1]) {
          case 's3tablescatalog':
            switch (resourceParts[0]) {
              case 'column':
                [, , bucketName, databaseName, tableName, columnName] = resourceParts;
                break;
              case 'table':
                [, , bucketName, databaseName, tableName] = resourceParts;
                break;
              case 'database':
                [, , bucketName, databaseName] = resourceParts;
                break;
            }
            break;
          default:
            switch (resourceParts[0]) {
              case 'column':
                [, databaseName, tableName, columnName] = resourceParts;
                break;
              case 'table':
                [, databaseName, tableName] = resourceParts;
                break;
              case 'database':
                [, databaseName] = resourceParts;
                break;
            }
            break;
        }
        break;

      case 'redshift':
        switch (resourceParts[0]) {
          case 'column':
            [, clusterId, databaseName, schemaName, tableName, columnName] = resourceParts;
            break;
          case 'table':
            [, clusterId, databaseName, schemaName, tableName] = resourceParts;
            break;
          case 'schema':
            [, clusterId, databaseName, schemaName] = resourceParts;
            break;
          case 'database':
            [, clusterId, databaseName] = resourceParts;
            break;
        }
        break;
    }
  }

  return {
    dataSource: dataSource,
    catalogId: catalogId,
    clusterId: clusterId,
    bucketName: bucketName,
    databaseName: databaseName,
    schemaName: schemaName,
    tableName: tableName,
    columnName: columnName,
    region: region,
  };
}

export function inferDataSourceFromIdInfo(idInfo: IdInfo) {
  if (idInfo.dataSource) {
    return idInfo.dataSource;
  }

  // Imperfectly attempt to guess the data source ID from the ID Info.
  // This is a best-effort guess and isn't guaranteed to be correct.
  if (idInfo.bucketName) {
    return S3_TABLE_DATASOURCE_ID;
  } else if (idInfo.redshiftWorkgroupName) {
    return DZ_REDSHIFT_DATASOURCE_ID;
  } else if (idInfo.clusterId || idInfo.schemaName) {
    return REDSHIFT_DATASOURCE_ID;
  } else if (IAM_CATALOG_IDS.includes(idInfo.catalogId)) {
    return IAM_DATASOURCE_ID;
  } else {
    return LAKE_FORMATION_DATASOURCE_ID;
  }
}
